/**
 * Service de atendimento (prontuário) — Central de Amparo.
 * Persiste o prontuário no Supabase na tabela cadastrada em support_center_forms.
 * Não finaliza o agendamento (markRealizado fica na agenda / Finalizar).
 */
import { supabase } from '../lib/supabase'
import { supportCenterAppointmentsService } from './supportCenterAppointments'
import { supportCenterFormsService } from './supportCenterForms'
import { supportCenterTriageCatalogService } from './supportCenterTriageCatalog'
import type { SupportCenterPendingAttendanceForm } from '../types/supportCenterPendingAttendanceForms'
import type { SupportCenterFormKind } from '../types/supportCenterSpecialties'
import type {
  SupportCenterAttendanceDemandSelection,
  SupportCenterAttendanceDemandType,
  SupportCenterAttendanceNotesRecord,
  SupportCenterPsychAttendancePayload,
  SupportCenterPsychAttendanceRecord,
  SupportCenterAttendanceSelectedDemandType,
} from '../types/supportCenterPsychAttendance'

const ATTENDANCE_TEXT_COLUMN_BY_KIND: Partial<
  Record<Exclude<SupportCenterFormKind, 'psicologico'>, string>
> = {
  nutricional: 'observations',
  fitness: 'observations',
  juridico: 'guidance',
  social: 'guidance',
}

function attendanceTextColumn(formKind: SupportCenterFormKind): string | null {
  if (formKind === 'psicologico') return null
  return ATTENDANCE_TEXT_COLUMN_BY_KIND[formKind] ?? null
}

function normalizeMedications(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const items: string[] = []
  for (const item of raw) {
    const name = String(item ?? '').trim()
    if (!name) continue
    const key = name.toLocaleLowerCase('pt-BR')
    if (seen.has(key)) continue
    seen.add(key)
    items.push(name)
  }
  return items
}

function psychRowPayload(payload: SupportCenterPsychAttendancePayload) {
  return {
    id: payload.appointment_id,
    medications: normalizeMedications(payload.medications),
    demand_notes: payload.demand_notes,
    intervention_notes: payload.intervention_notes,
    patient_response: payload.patient_response,
    emotional_state: payload.emotional_state,
    risk_indicator: payload.risk_indicator,
    referral: payload.referral,
    next_contact_date: payload.next_contact_date,
    additional_notes: payload.additional_notes,
  }
}

function normalizeDemandSelections(
  selections: SupportCenterAttendanceDemandSelection
): SupportCenterAttendanceDemandSelection {
  const unique = new Map<string, SupportCenterAttendanceDemandSelection[number]>()
  for (const item of selections) {
    const demand_type_id = String(item.demand_type_id ?? '').trim()
    if (!demand_type_id) continue
    unique.set(demand_type_id, {
      demand_type_id,
      notes: String(item.notes ?? ''),
    })
  }
  return [...unique.values()]
}

function mapAttendanceDemandTypes(raw: unknown): SupportCenterAttendanceSelectedDemandType[] {
  if (!Array.isArray(raw)) return []
  const unique = new Map<string, SupportCenterAttendanceSelectedDemandType>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const relation = item as { demand_type?: unknown; notes?: unknown }
    const demandRaw = Array.isArray(relation.demand_type) ? relation.demand_type[0] : relation.demand_type
    if (!demandRaw || typeof demandRaw !== 'object') continue
    const demand = demandRaw as {
      id?: string
      name?: string | null
      requires_notes?: boolean | null
    }
    const notes = relation.notes
    const id = String(demand.id ?? '').trim()
    if (!id) continue
    unique.set(id, {
      id,
      name: String(demand.name ?? '').trim(),
      requires_notes: Boolean(demand.requires_notes),
      notes: typeof notes === 'string' ? notes : null,
    })
  }
  return [...unique.values()]
}

async function getAttendanceDemandSelection(
  appointmentId: string
): Promise<{
  demand_selections: SupportCenterAttendanceDemandSelection
  demand_types: SupportCenterAttendanceSelectedDemandType[]
}> {
  const { data, error } = await supabase
    .from('support_center_attendance_demand_types')
    .select(
      `
      notes,
      demand_type_id,
      demand_type:support_center_demand_types!demand_type_id(id, name, requires_notes)
    `
    )
    .eq('appointment_id', appointmentId)

  if (error) throw error
  const rows = (data ?? []) as Array<{ demand_type_id?: string; notes?: string | null }>
  return {
    demand_selections: normalizeDemandSelections(
      rows.map((row) => ({
        demand_type_id: String(row.demand_type_id ?? ''),
        notes: row.notes ?? '',
      }))
    ),
    demand_types: mapAttendanceDemandTypes(data),
  }
}

async function syncAttendanceDemandTypes(
  appointmentId: string,
  demandSelections: SupportCenterAttendanceDemandSelection
): Promise<void> {
  const unique = normalizeDemandSelections(demandSelections)

  const { error: deleteError } = await supabase
    .from('support_center_attendance_demand_types')
    .delete()
    .eq('appointment_id', appointmentId)
  if (deleteError) throw deleteError

  if (unique.length === 0) return

  const { error: insertError } = await supabase
    .from('support_center_attendance_demand_types')
    .insert(
      unique.map(({ demand_type_id, notes }) => ({
        appointment_id: appointmentId,
        demand_type_id,
        notes: notes.trim() || null,
      }))
    )
  if (insertError) throw insertError
}

async function assertPsychNotSubmitted(appointmentId: string): Promise<void> {
  const current = await supportCenterAttendService.getPsychologicalAttendance(appointmentId)
  if (current?.submitted_at) {
    throw new Error('Formulário já enviado; não é possível alterar.')
  }
}

async function assertNotesNotSubmitted(
  formKind: SupportCenterFormKind,
  appointmentId: string
): Promise<void> {
  const current = await supportCenterAttendService.getAttendanceNotes(formKind, appointmentId)
  if (current?.submitted_at) {
    throw new Error('Formulário já enviado; não é possível alterar.')
  }
}

async function loadAttendanceFormStatusByIds(appointmentIds: string[]): Promise<{
  submittedIds: Set<string>
  draftIds: Set<string>
}> {
  const submittedIds = new Set<string>()
  const draftIds = new Set<string>()
  if (appointmentIds.length === 0) {
    return { submittedIds, draftIds }
  }

  const forms = await supportCenterFormsService.list()
  const tables = [
    ...new Set(
      forms
        .map((form) => form.table_name?.trim())
        .filter((name): name is string => Boolean(name))
    ),
  ]

  for (const table of tables) {
    const { data, error } = await supabase
      .from(table)
      .select('id, submitted_at')
      .in('id', appointmentIds)

    if (error) {
      // Tabela cadastrada mas ausente no banco — ignora e segue.
      if (error.code === '42P01' || /does not exist/i.test(error.message)) {
        continue
      }
      throw error
    }

    for (const row of data ?? []) {
      const id = String((row as { id?: string }).id ?? '')
      if (!id) continue
      const submittedAt = (row as { submitted_at?: string | null }).submitted_at
      if (submittedAt) {
        submittedIds.add(id)
      } else {
        draftIds.add(id)
      }
    }
  }

  return { submittedIds, draftIds }
}

/** Marca attendance_form_submitted nos eventos com atendimento iniciado. */
export async function enrichAgendaEventsFormSubmitted<
  T extends { id: string; attendance_started_at?: string | null },
>(events: T[]): Promise<Array<T & { attendance_form_submitted: boolean }>> {
  const startedIds = events
    .filter((event) => Boolean(event.attendance_started_at?.trim()))
    .map((event) => event.id)
  if (startedIds.length === 0) {
    return events.map((event) => ({ ...event, attendance_form_submitted: false }))
  }
  const { submittedIds } = await loadAttendanceFormStatusByIds(startedIds)
  return events.map((event) => ({
    ...event,
    attendance_form_submitted: submittedIds.has(event.id),
  }))
}

export const supportCenterAttendService = {
  async listDemandTypesByAssistance(assistanceId: string | null | undefined): Promise<SupportCenterAttendanceDemandType[]> {
    const id = String(assistanceId ?? '').trim()
    if (!id) return []
    const demandTypes = await supportCenterTriageCatalogService.listDemandTypes()
    return demandTypes
      .filter((item) => item.assistances.some((assistance) => assistance.id === id))
      .map((item) => ({
        id: item.id,
        name: item.name,
        requires_notes: item.requires_notes,
      }))
  },

  /**
   * Atendimentos iniciados ainda abertos, sem formulário enviado.
   * has_draft = já existe rascunho (submitted_at nulo).
   */
  async listPendingForms(filters?: {
    professional_user_id?: string | null
    specialization_id?: string | null
  }): Promise<SupportCenterPendingAttendanceForm[]> {
    const started = await supportCenterAppointmentsService.listStartedOpen({
      professional_user_id: filters?.professional_user_id,
      specialization_id: filters?.specialization_id,
    })
    if (started.length === 0) return []

    const ids = started.map((row) => row.id)
    const { submittedIds, draftIds } = await loadAttendanceFormStatusByIds(ids)

    return started
      .filter((appointment) => !submittedIds.has(appointment.id))
      .map((appointment) => ({
        appointment,
        has_draft: draftIds.has(appointment.id),
      }))
  },

  async getPsychologicalAttendance(
    appointmentId: string
  ): Promise<SupportCenterPsychAttendanceRecord | null> {
    const table = await supportCenterFormsService.getAttendanceTableName('psicologico')
    const { data, error } = await supabase
      .from(table)
      .select(
        'medications, demand_notes, intervention_notes, patient_response, emotional_state, risk_indicator, referral, next_contact_date, additional_notes, submitted_at'
      )
      .eq('id', appointmentId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const row = data as Record<string, string | null> & { medications?: unknown }
    const selection = await getAttendanceDemandSelection(appointmentId)
    return {
      demand_selections: selection.demand_selections,
      demand_types: selection.demand_types,
      medications: normalizeMedications(row.medications),
      demand_notes: row.demand_notes ?? null,
      intervention_notes: row.intervention_notes ?? null,
      patient_response: row.patient_response ?? null,
      emotional_state: row.emotional_state ?? null,
      risk_indicator: row.risk_indicator ?? null,
      referral: row.referral ?? null,
      next_contact_date: row.next_contact_date ?? null,
      additional_notes: row.additional_notes ?? null,
      submitted_at: row.submitted_at ?? null,
    }
  },

  /** Upsert de rascunho — não define submitted_at e não finaliza o agendamento. */
  async savePsychAttendanceDraft(payload: SupportCenterPsychAttendancePayload): Promise<void> {
    await assertPsychNotSubmitted(payload.appointment_id)
    const table = await supportCenterFormsService.getAttendanceTableName('psicologico')
    const { error } = await supabase.from(table).upsert(psychRowPayload(payload), {
      onConflict: 'id',
    })
    if (error) throw error
    await syncAttendanceDemandTypes(payload.appointment_id, payload.demand_selections)
  },

  /** Envia o formulário (trava com submitted_at). Não finaliza o agendamento. */
  async submitPsychAttendance(payload: SupportCenterPsychAttendancePayload): Promise<void> {
    await assertPsychNotSubmitted(payload.appointment_id)
    const table = await supportCenterFormsService.getAttendanceTableName('psicologico')
    const { error } = await supabase.from(table).upsert(
      {
        ...psychRowPayload(payload),
        submitted_at: new Date().toISOString(),
      },
      { onConflict: 'id' }
    )
    if (error) throw error
    await syncAttendanceDemandTypes(payload.appointment_id, payload.demand_selections)
  },

  async getAttendanceNotes(
    formKind: SupportCenterFormKind,
    appointmentId: string
  ): Promise<SupportCenterAttendanceNotesRecord | null> {
    const column = attendanceTextColumn(formKind)
    if (!column) return null

    const table = await supportCenterFormsService.getAttendanceTableName(formKind)
    const { data, error } = await supabase
      .from(table)
      .select(`${column}, submitted_at`)
      .eq('id', appointmentId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const row = data as unknown as Record<string, string | null>
    const notes = row[column]?.trim() || null
    const selection = await getAttendanceDemandSelection(appointmentId)
    return {
      demand_selections: selection.demand_selections,
      demand_types: selection.demand_types,
      notes,
      submitted_at: row.submitted_at ?? null,
    }
  },

  /** Upsert de rascunho de texto — não define submitted_at e não finaliza. */
  async saveAttendanceNotesDraft(
    formKind: SupportCenterFormKind,
    appointmentId: string,
    notes: string,
    demandSelections: SupportCenterAttendanceDemandSelection
  ): Promise<void> {
    const column = attendanceTextColumn(formKind)
    if (!column) throw new Error('Formulário de atendimento não suportado.')

    await assertNotesNotSubmitted(formKind, appointmentId)
    const table = await supportCenterFormsService.getAttendanceTableName(formKind)
    const { error } = await supabase.from(table).upsert(
      {
        id: appointmentId,
        [column]: notes.trim(),
      },
      { onConflict: 'id' }
    )
    if (error) throw error
    await syncAttendanceDemandTypes(appointmentId, demandSelections)
  },

  /** Envia o formulário de texto (trava com submitted_at). Não finaliza o agendamento. */
  async submitAttendanceNotes(
    formKind: SupportCenterFormKind,
    appointmentId: string,
    notes: string,
    demandSelections: SupportCenterAttendanceDemandSelection
  ): Promise<void> {
    const column = attendanceTextColumn(formKind)
    if (!column) throw new Error('Formulário de atendimento não suportado.')

    const text = notes.trim()
    if (!text) throw new Error('Informe o texto do atendimento.')

    await assertNotesNotSubmitted(formKind, appointmentId)
    const table = await supportCenterFormsService.getAttendanceTableName(formKind)
    const { error } = await supabase.from(table).upsert(
      {
        id: appointmentId,
        [column]: text,
        submitted_at: new Date().toISOString(),
      },
      { onConflict: 'id' }
    )
    if (error) throw error
    await syncAttendanceDemandTypes(appointmentId, demandSelections)
  },
}
